/**
 * Tema del calendari (`plans/calendar-visual-refresh.md` §3-§4, D7).
 *
 * Un sol lloc per als colors de la graella i de les cards. Només els colors dels canals són
 * configurables (a `/config` → «Canals», F5); la resta viu aquí, amb les ràtios de contrast del §3.
 * El gruix de la barra i la densitat surten de la roda dentada del calendari (per dispositiu).
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useChannelConfigs, type ChannelConfig } from '@/hooks/useChannelConfigs';
import { useCalendarSettings } from './CalendarSettingsDialog';
import { CARD_RADIUS_PX, RIGHT_RADIUS_PX } from './calendar-radius';
import { ROW_HEIGHT_PX } from './grid/row-layout';

/** Hex del §3. Qualsevol canvi ha de mantenir les ràtios de la taula de l'spec. */
export const CALENDAR_PALETTE = {
  /** Cel·la d'un dia lliure */
  cell: '#EEF2F6',
  /** Cel·la de DV i DS (D5) */
  cellWeekend: '#E2E8F0',
  /** Cel·la d'avui */
  cellToday: '#DBEAFE',
  /** Línia vertical d'avui (2 px, tota l'alçada) */
  todayLine: '#2563EB',
  /** Fila en hover (cel·les lliures) */
  rowHover: '#E2E8F0',
  /** Separador entre dies i entre files */
  separator: '#CBD5E1',
  /** Capçalera normal */
  header: '#F8FAFC',
  /** Capçalera de DV i DS */
  headerWeekend: '#E2E8F0',
  /** Capçalera d'avui */
  headerToday: '#DBEAFE',
  /** Text de la capçalera d'avui */
  headerTodayText: '#1E40AF',
  /** Fons de la card (sempre blanc, D1) */
  card: '#FFFFFF',
  /** Vora de la card (1 px + `shadow-sm`) */
  cardBorder: '#CBD5E1',
  /** Fons de la card cancel·lada (amb opacitat 60 % i tot ratllat) */
  cardCancelled: '#FEE2E2',
  /** Text de la línia 2 de la card (11 px) */
  cardSecondaryText: '#475569',
  /** Vora puntejada dels llits automàtics */
  bedAutoBorder: '#64748B',
  /** Etiquetes del popover (11 px) */
  popoverLabel: '#6B7280',
} as const;

/**
 * Barra de canal per defecte (§3). Totes ≥ 3:1 contra blanc menys Expedia, que és el groc de la
 * marca (`#EEC218`, 1,70:1): en Pere el vol reconeixible (29-SET). Airbnb/Agoda/Hotels.com, com eren.
 */
export const DEFAULT_CHANNEL_COLORS: Record<string, string> = {
  'Booking.com': '#003580',
  Expedia: '#EEC218',
  Direct: '#15803D',
  Directe: '#15803D',
  Airbnb: '#FF5A5F',
  Agoda: '#E02E2E',
  'Hotels.com': '#D32F2F',
  default: '#64748B',
};

/** En Pere (29-SET): més gruixuda que els 6 px de l'spec; a la roda dentada, de 4 a 12. */
export const DEFAULT_BAR_WIDTH = 8;
/** Cantonades de l'esquerra (entrada) per defecte (`calendar-radius.ts`). */
export const DEFAULT_CARD_RADIUS = CARD_RADIUS_PX.large;
/** Cantonades de la dreta per defecte: rectes (`plans/calendar-card-signals.md` D3). */
export const DEFAULT_RIGHT_RADIUS = RIGHT_RADIUS_PX.square;
export const CELL_WIDTH_NORMAL = 200;
export const CELL_WIDTH_COMPACT = 140;
/** Alçada de fila per defecte (Compacta, F7): la de la configuració per defecte. */
export const DEFAULT_ROW_HEIGHT = ROW_HEIGHT_PX.compact;

export interface CalendarTheme {
  /** Color (hex) de la barra del canal; el de `default` si no el coneix. */
  channelColor: (channel: string | null | undefined) => string;
  /** Gruix de la barra del canal, en px. */
  barWidth: number;
  /** Amplada d'una columna de dia, en px. */
  cellWidth: number;
  /** Radi de les cantonades de l'esquerra (entrada) de la reserva, en px (`stayCornerStyle`). */
  cardRadius: number;
  /** Radi de les cantonades de la dreta (sortida), en px (`stayCornerStyle`, D3). */
  rightRadius: number;
  /** Alçada d'una fila d'habitació, en px (Normal 75 · Compacta 60 · Mínima 46, F7). */
  rowHeight: number;
}

/**
 * Clau de casament d'un nom de canal: minúscules, sense el prefix `A-` de les reserves velles de
 * Beds24 (`A-Expedia`), sense `.com` i sense espais ni signes. Així «Booking.com», «BOOKING» i
 * «booking» cauen al mateix lloc, i «Hostelworld» i «Hostelworld.com» també.
 */
export function normalizeChannelKey(channel: string | null | undefined): string {
  return (channel ?? '')
    .trim()
    .toLowerCase()
    .replace(/^a-/, '')
    .replace(/\.com$/, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Noms que arriben a `reservations.channel` (DB local, 29-SET) i que pinten com un altre canal
 * de la taula `channel_config` si no en tenen fila pròpia. Claus ja normalitzades.
 * Les marques del grup Expedia (EAN, Orbitz, Travelocity, Wotif, Egencia) van amb Expedia;
 * Hotels.com té fila pròpia i no hi és.
 */
export const CHANNEL_ALIASES: Record<string, string> = {
  directe: 'direct',
  expediaaffiliatenetwork: 'expedia',
  orbitz: 'expedia',
  travelocity: 'expedia',
  wotif: 'expedia',
  egencia: 'expedia',
};

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function indexColors(entries: Array<[string, string]>): Map<string, string> {
  const map = new Map<string, string>();
  for (const [name, color] of entries) {
    const key = normalizeChannelKey(name);
    if (!key || key === 'default' || !HEX_COLOR.test(color.trim())) continue;
    if (!map.has(key)) map.set(key, color.trim());
  }
  return map;
}

const DEFAULT_COLOR_INDEX = indexColors(Object.entries(DEFAULT_CHANNEL_COLORS));

/**
 * `channelColor` a partir de les files de `/config` → «Canals». Ordre: la fila del canal (habilitada,
 * amb un hex vàlid), la fila del canal de l'àlies, el per defecte del canal, el de l'àlies i, si
 * res no casa, `DEFAULT_CHANNEL_COLORS.default`.
 */
export function createChannelColorResolver(
  configs?: ReadonlyArray<Pick<ChannelConfig, 'channelName' | 'backgroundColor' | 'enabled'>> | null
): CalendarTheme['channelColor'] {
  const fromConfig = indexColors(
    (configs ?? [])
      .filter((c) => c && c.enabled !== false && typeof c.backgroundColor === 'string')
      .map((c) => [c.channelName, c.backgroundColor])
  );
  return (channel) => {
    const key = normalizeChannelKey(channel);
    const alias = CHANNEL_ALIASES[key];
    return (
      fromConfig.get(key) ??
      (alias ? fromConfig.get(alias) : undefined) ??
      DEFAULT_COLOR_INDEX.get(key) ??
      (alias ? DEFAULT_COLOR_INDEX.get(alias) : undefined) ??
      DEFAULT_CHANNEL_COLORS.default
    );
  };
}

export const DEFAULT_CALENDAR_THEME: CalendarTheme = {
  channelColor: createChannelColorResolver(),
  barWidth: DEFAULT_BAR_WIDTH,
  cellWidth: CELL_WIDTH_NORMAL,
  cardRadius: DEFAULT_CARD_RADIUS,
  rightRadius: DEFAULT_RIGHT_RADIUS,
  rowHeight: DEFAULT_ROW_HEIGHT,
};

const CalendarThemeContext = createContext<CalendarTheme | null>(null);

/**
 * F5: els colors dels canals surten de `/config` → «Canals» (`GET /calendar-config/channels`) i,
 * mentre carrega o si falla, dels per defecte; el gruix de la barra, la densitat i l'alçada de les files, de la roda
 * dentada (`useCalendarSettings`, compartit: un canvi arriba aquí de seguida).
 */
export function CalendarThemeProvider({ children }: { children: ReactNode }) {
  const { data: configs } = useChannelConfigs();
  const { settings } = useCalendarSettings();
  const { barWidth, density, cardRadius, rightRadius, rowHeight } = settings;

  const channelColor = useMemo(
    () => createChannelColorResolver(Array.isArray(configs) ? configs : null),
    [configs]
  );
  const value = useMemo<CalendarTheme>(
    () => ({
      channelColor,
      barWidth,
      cellWidth: density === 'compact' ? CELL_WIDTH_COMPACT : CELL_WIDTH_NORMAL,
      cardRadius: CARD_RADIUS_PX[cardRadius],
      rightRadius: RIGHT_RADIUS_PX[rightRadius],
      rowHeight: ROW_HEIGHT_PX[rowHeight],
    }),
    [channelColor, barWidth, density, cardRadius, rightRadius, rowHeight]
  );

  return <CalendarThemeContext.Provider value={value}>{children}</CalendarThemeContext.Provider>;
}

/** Fora del provider torna els valors per defecte (tests i components solts no es trenquen). */
export function useCalendarTheme(): CalendarTheme {
  return useContext(CalendarThemeContext) ?? DEFAULT_CALENDAR_THEME;
}
